/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { SearchApi } from '@alfresco/js-api';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { JobDefinition, JobRun, JobsService } from '../jobs/jobs.service';

export type ExpiryStatus = 'expired' | 'soon' | 'active';

/** The unattended expiry job the jobs service runs, and how it is doing. */
export interface ExpiryAutomation {
  /** Whether the jobs service answers at all; without it nothing runs. */
  service: boolean;
  /** The job, once one has been set up. */
  job: JobDefinition | null;
  /** When the scheduler next runs it. */
  nextRun: string | null;
  running: boolean;
  /** Its most recent run. */
  lastRun: JobRun | null;
}

export interface ExpiryDoc {
  id: string;
  name: string;
  path: string;
  expiry: string; // ISO date (cm:to)
  /** Shown by the list; empty when the repository has no value for it. */
  owner?: string;
  documentType?: string;
  documentCode?: string;
  sizeInBytes?: number | null;
  modifiedAt?: string;
  status: ExpiryStatus;
}

/** Number of days before the expiry date that a document is flagged "expiring soon". */
export const EXPIRY_SOON_DAYS = 30;

/** The jobs-service action that archives expired documents — see Jobs/JobRunner.cs. */
export const EXPIRE_ACTION = 'expire';

/**
 * Document Expiry management. The expiry date is stored in the standard
 * `cm:to` property (part of the `cm:effectivity` aspect), so no custom content model
 * is required. Disposing a document sends it to the trash, where it stays
 * restorable until the trash is emptied.
 */
@Injectable({ providedIn: 'root' })
export class ExpiryService {
  private readonly api = inject(AlfrescoApiService);
  private readonly jobs = inject(JobsService);
  private get searchApi(): SearchApi {
    return new SearchApi(this.api.getInstance() as any);
  }

  /** Core public API (contentClient.basePath = `{ecm}/.../public/alfresco/versions/1`). */
  private call(path: string, method: string, body?: any, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(
      inst.contentClient.callApi(path, method, {}, query ?? {}, {}, {}, body ?? null, ['application/json'], ['application/json']) as Promise<any>
    );
  }

  /** Classify a `cm:to` value relative to now. */
  static classify(expiryIso: string): ExpiryStatus {
    const now = Date.now();
    const t = new Date(expiryIso).getTime();
    if (t < now) {
      return 'expired';
    }
    if (t <= now + EXPIRY_SOON_DAYS * 24 * 60 * 60 * 1000) {
      return 'soon';
    }
    return 'active';
  }

  /** All documents that have an expiry date set, newest-expiry first-to-expire. */
  listWithExpiry(maxItems = 500): Observable<ExpiryDoc[]> {
    return from(
      this.searchApi.search({
        query: { query: '+TYPE:"cm:content" +EXISTS:"cm:to"', language: 'afts' },
        include: ['properties', 'path'],
        paging: { maxItems, skipCount: 0 },
        sort: [{ type: 'FIELD', field: 'cm:to', ascending: true }]
      }) as Promise<any>
    ).pipe(
      map((res) =>
        (res?.list?.entries || [])
          .map((e: any) => e.entry)
          .filter((n: any) => n?.properties?.['cm:to'])
          .map((n: any): ExpiryDoc => {
            const expiry = n.properties['cm:to'];
            return {
              id: n.id,
              name: n.name,
              path: n.path?.name || '',
              expiry,
              modifiedAt: n.modifiedAt,
              status: ExpiryService.classify(expiry),
              owner: n.modifiedByUser?.displayName || n.createdByUser?.displayName || '',
              documentType: n.properties?.['dms:documentType'] || '',
              documentCode: n.properties?.['dms:documentCode'] || '',
              sizeInBytes: n.content?.sizeInBytes ?? null
            };
          })
      )
    );
  }

  /**
   * The unattended expiry job and its state.
   *
   * It is an ordinary jobs-service job whose action is `expire`: every night it
   * moves the documents whose expiry date has passed into the Archive folder,
   * keeping each one's readers, and marks them expired. The definition lives
   * with the other jobs in "Data Dictionary/DMS Jobs", so the jobs page shows
   * and edits it too.
   */
  automation(): Observable<ExpiryAutomation> {
    return forkJoin({
      health: this.jobs.health(),
      jobs: this.jobs.list().pipe(catchError(() => of([] as JobDefinition[])))
    }).pipe(
      switchMap(({ health, jobs }) => {
        const job = jobs.find((j) => j.action?.type === EXPIRE_ACTION) ?? null;
        const base: ExpiryAutomation = { service: !!health, job, nextRun: null, running: false, lastRun: null };
        if (!job) {
          return of(base);
        }
        return forkJoin({ states: this.jobs.scheduleState(), runs: this.jobs.runs(30) }).pipe(
          map(({ states, runs }) => {
            const state = states.find((s) => s.id === job.id);
            return {
              ...base,
              nextRun: state?.nextRun ?? null,
              running: !!state?.running,
              lastRun: runs.find((r) => r.jobId === job.id) ?? null
            };
          })
        );
      })
    );
  }

  /**
   * Turns the expiry job on or off. Turning it on the first time creates it:
   * a nightly run, for real rather than rehearsed, since its query is fixed by
   * the sidecar and reaches only documents whose date has passed.
   */
  setAutomation(current: ExpiryAutomation, enabled: boolean, name: string): Observable<string> {
    return this.jobs.save(current.job ? { ...current.job, enabled } : { ...ExpiryService.expiryJob(name), enabled });
  }

  /** Runs the expiry job now rather than waiting for the night. */
  runAutomation(jobId: string): Observable<boolean> {
    return this.jobs.runNow(jobId);
  }

  /** The definition the expiry job starts from. */
  static expiryJob(name: string): JobDefinition {
    return {
      name,
      enabled: true,
      dryRun: false,
      schedule: { cron: '0 0 1 * * *', timezone: 'Asia/Bangkok' },
      condition: { nodeType: 'cm:content', rules: [] },
      action: { type: EXPIRE_ACTION },
      notify: { onComplete: false, onlyWhenMatched: true, recipients: [] }
    };
  }

  /** Set (or with `null`, clear) the expiry date on a node. Applies `cm:effectivity`. */
  setExpiry(nodeId: string, expiryIso: string | null): Observable<any> {
    return this.call(`/nodes/${nodeId}`, 'GET', null, { include: 'aspectNames' }).pipe(
      switchMap((res) => {
        const aspects = new Set<string>(res?.entry?.aspectNames || []);
        aspects.add('cm:effectivity');
        return this.call(`/nodes/${nodeId}`, 'PUT', {
          aspectNames: [...aspects],
          properties: { 'cm:to': expiryIso }
        });
      })
    );
  }

  /** Send a node to the trash — a soft delete, restorable from the trashcan. */
  trash(nodeId: string): Observable<any> {
    return this.call(`/nodes/${nodeId}`, 'DELETE');
  }
}
